import { t } from '@/features/i18n/utils';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useAcceptInvitation } from '@/features/organizations/mutations';
import { Logo } from '@/ui/components/logo';
import { Button } from '@/ui/components/button';
import { useNavigate } from '@tanstack/react-router';
import z from 'zod';

export const Route = createFileRoute('/_authed/accept-invitation')({
  component: RouteComponent,
  validateSearch: z.object({
    invitationToken: z.string().optional(),
    organizationId: z.string().optional(),
    organizationName: z.string().optional(),
  }),
});

function RouteComponent() {
  const searchParams = Route.useSearch();
  const navigate = useNavigate();

  const invitationToken = searchParams.invitationToken ?? '';
  const organizationId = searchParams.organizationId ?? '';
  const organizationName = searchParams.organizationName ?? '';
  const isUrlError = !invitationToken || !organizationId || !organizationName;

  const { isPending, mutate } = useAcceptInvitation();

  return (
    <div className="px-5 -translate-y-5 w-full my-auto">
      <Logo className="mb-5 mx-auto" />
      {isUrlError ? (
        <div className="text-center">
          <div className="text-center pb-3">
            {t({
              en: 'There was something wrong with the URL, please make sure that you are using a complete link you received, or redirect to the homepage using the link below.',
              nb: 'Det var noe galt med URL-en, vennligst sørg for at du bruker en komplett lenke du mottok, eller gå tilbake til hjemmesiden ved hjelp av lenken nedenfor.',
            })}
          </div>
          <Link to="/" className="underline hover:text-foreground text-muted">
            {t({ en: 'Back to Homepage', nb: 'Tilbake til hjemmesiden' })}
          </Link>
        </div>
      ) : (
        <>
          <p className="text-center font-semibold text-lg mb-0.5 lg:text-xl">
            {t({
              en: (
                <>
                  You've been invited to <strong>{organizationName}</strong>
                </>
              ),
              nb: (
                <>
                  Du er invitert til <strong>{organizationName}</strong>
                </>
              ),
            })}
          </p>
          <p className="text-center text-muted text-sm mb-4">
            {t({
              en: 'Confirm the invitation using the button below.',
              nb: 'Bekreft invitasjonen ved hjelp av knappen nedenfor.',
            })}
          </p>
          <div className="flex flex-col align-content-center">
            <Button
              pending={isPending}
              className="w-fit mx-auto"
              onClick={() =>
                mutate(invitationToken, {
                  onSuccess: () =>
                    navigate({ to: '/', search: { inviteAccepted: true, organizationName } }),
                })
              }
            >
              {t({ en: 'Accept', nb: 'Aksepter' })}
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
